import { useEffect } from 'react';
import type { AnalyticsEventType } from '../lib/api';
import { recordInteraction } from '../lib/analyticsSender';

interface UseTimeOnCardOptions {
  eventType?: AnalyticsEventType;
  minDurationMs?: number;
}

export function useTimeOnCard(
  itemId?: string | number | null,
  options?: UseTimeOnCardOptions
): void {
  const eventType = options?.eventType ?? 'view';
  const minDurationMs = options?.minDurationMs ?? 1000;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    if (!itemId) {
      return;
    }

    const trackedItemId = String(itemId);
    let startTime = performance.now();
    let accumulated = 0;
    let isTracking = document.visibilityState !== 'hidden';

    if (!isTracking) {
      startTime = 0;
    }

    const pause = () => {
      if (!isTracking) {
        return;
      }
      accumulated += performance.now() - startTime;
      isTracking = false;
    };

    const resume = () => {
      if (isTracking) {
        return;
      }
      startTime = performance.now();
      isTracking = true;
    };

    const flush = () => {
      pause();
      if (accumulated < minDurationMs) {
        return;
      }
      recordInteraction({
        itemId: trackedItemId,
        eventType,
        durationMs: Math.round(accumulated),
      });
      accumulated = 0;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        pause();
      } else {
        resume();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, [itemId, eventType, minDurationMs]);
}






